import { computed, ref } from 'vue';

import {
  ApiError,
  api,
  onSessionRefreshed,
  refreshSession,
  type PublicPlayer,
} from '@/net/api';

/**
 * Who is signed in.
 *
 * A single module-scoped store rather than a provide/inject tree: there is
 * exactly one session per tab, and passing it down through three screens would
 * be ceremony around a global that is genuinely global.
 */
/**
 * A hint, not a credential.
 *
 * The refresh cookie is httpOnly, so the page cannot tell whether it has one.
 * Without this flag every cold visit fires a `POST /api/auth/refresh` that a
 * first-time visitor is guaranteed to get a 401 from — a wasted round trip and
 * a red line in their console. The flag says only "this browser signed in at
 * some point"; it grants nothing, and the cookie remains the sole authority.
 */
const SESSION_HINT = 'sow.session';

const player = ref<PublicPlayer | null>(null);
const restoring = ref(true);
const busy = ref(false);
const error = ref<string | null>(null);

/**
 * The game profile's call sign, once the garage or a rename has told us it.
 *
 * The template's auth only knows `username`, so every session it hands back —
 * login, `sync`, and the silent refresh every 15 minutes — names the player by
 * that. The call sign the battle and the lobby chat stamp is the profile's, and
 * it differs for anyone who renamed or whose username was sanitised; the chat's
 * own-name highlight compares the two, so a stale one leaves the player looking
 * like everybody else. Kept per id, so it can never leak onto another account.
 */
let callSign: { id: string; nickname: string } | null = null;

/** Installs a session from the server, keeping the known call sign for that player. */
function adopt(next: PublicPlayer): void {
  player.value = callSign?.id === next.id ? { ...next, nickname: callSign.nickname } : next;
}

onSessionRefreshed((session) => {
  adopt(session.player);
  localStorage.setItem(SESSION_HINT, '1');
});

export function useSession() {
  return {
    player: computed(() => player.value),
    signedIn: computed(() => player.value !== null),
    restoring: computed(() => restoring.value),
    busy: computed(() => busy.value),
    error: computed(() => error.value),
    register,
    login,
    logout,
    restore,
    sync,
    clearError,
    setBalances,
    setNickname,
  };
}

/**
 * Re-reads who is signed in, from the server, right now.
 *
 * **Why this is not `refreshSession()`.** That function deduplicates concurrent
 * callers through a module-level `refreshing` promise, which is correct for its
 * job — several 401s must not race each other — and wrong for this one: after
 * redeeming a verification token the caller needs an answer that reflects the
 * redemption, and the dedupe will happily hand back an in-flight refresh that
 * *started before it*. That is exactly what happened. `App.vue` fires
 * `restore()` on mount, `VerifyEmailScreen` redeems the token and then asked for
 * a refresh, got the mount's promise back carrying `emailVerified: false`, and
 * the app dropped onto the verification gate it had just been let through. Found
 * by `e2e/cases/profile`, which timed out waiting for a garage tab.
 *
 * `GET /api/auth/me` has no such sharing, and `JwtStrategy.validate` reads the
 * column from the database on every request — so this is always the current
 * answer, with no new token needed.
 *
 * Failure is a `false` and no state change: a link opened in a browser with no
 * session is a real case, and the right outcome there is "sign in", not an error.
 */
async function sync(): Promise<boolean> {
  try {
    const { player: current } = await api.me();
    adopt(current);
    localStorage.setItem(SESSION_HINT, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Called once at start-up. The access token died with the last page, but the
 * refresh cookie did not, so a signed-in player lands straight in the garage.
 */
async function restore(): Promise<void> {
  restoring.value = true;
  try {
    if (localStorage.getItem(SESSION_HINT) !== '1') return;
    // The cookie can still have expired or been revoked since.
    if (!(await refreshSession())) localStorage.removeItem(SESSION_HINT);
  } finally {
    restoring.value = false;
  }
}

/**
 * `altcha` is the solved proof-of-work payload from the widget on the join
 * screen — the template's auth requires one on both calls. The call sign is
 * posted as the template's `username`.
 */
async function register(input: {
  nickname: string;
  email: string;
  password: string;
  altcha: string;
}): Promise<boolean> {
  return run(() =>
    api.register({
      username: input.nickname,
      email: input.email,
      password: input.password,
      altcha: input.altcha,
    }),
  );
}

async function login(input: {
  email: string;
  password: string;
  altcha: string;
}): Promise<boolean> {
  return run(() => api.login(input));
}

async function logout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Signing out locally matters more than the server acknowledging it.
  }
  player.value = null;
  callSign = null;
  localStorage.removeItem(SESSION_HINT);
}

/**
 * Keeps the header balances in step after a purchase, without a second fetch.
 *
 * Fuel is deliberately absent: it is derived from the clock, so a copy held
 * here would be wrong the moment it was written. The garage store holds the
 * server's snapshot and the time it arrived, and counts down from that.
 */
function setBalances(credits: number, diamonds: number): void {
  if (player.value) player.value = { ...player.value, credits, diamonds };
}

/**
 * Keeps the header (and everything else reading `useSession().player`) in
 * step after a call-sign change, on the same reasoning as `setBalances`.
 *
 * `state/account.ts`'s `profile` ref is a *second* copy of the player's own
 * name — the one the Account pane itself reads — and `saveCallSign` already
 * updates that one from the server's response. It never touched this store,
 * so the header, the battle join payload (`App.vue`'s `nickname`) and the
 * in-battle roster all kept the name this session started with until the
 * access token's next silent refresh, up to 15 minutes later.
 */
function setNickname(nickname: string): void {
  if (!player.value) return;
  callSign = { id: player.value.id, nickname };
  player.value = { ...player.value, nickname };
}

function clearError(): void {
  error.value = null;
}

async function run(action: () => Promise<{ player: PublicPlayer }>) {
  busy.value = true;
  error.value = null;
  try {
    const session = await action();
    adopt(session.player);
    localStorage.setItem(SESSION_HINT, '1');
    return true;
  } catch (err) {
    error.value =
      err instanceof ApiError ? err.message : 'Could not reach the server. Is it running?';
    return false;
  } finally {
    busy.value = false;
  }
}
